/**
 * Edit Operation Grouping Rules
 * Handles grouping for text edits, property changes, and node CRUD operations
 */

import { Operation, UIOperation, NodeOperation } from '@/shared/types/projectData';
import { GroupingRule } from './transformRules';

export const editRules: Record<string, GroupingRule> = {
  // Text and property editing
  'text-edit': {
    opensGroup: 'edit',
    name: (op: Operation) => {
      const field = (op as UIOperation).details?.field || 'text';
      return `Edit ${field}`;
    },
    mergeWindow: 3000, // Longer window for typing
    mergeCondition: (lastOp: Operation, newOp: Operation) => {
      // Group edits within same field
      return (lastOp as UIOperation).details?.field === (newOp as UIOperation).details?.field;
    }
  },

  'toggle-change': {
    opensGroup: 'edit',
    name: (op: Operation) => {
      const field = (op as UIOperation).details?.field || 'property';
      return `Toggle ${field}`;
    },
    mergeWindow: 1000
  },

  'select-change': {
    opensGroup: 'edit',
    name: (op: Operation) => {
      const field = (op as UIOperation).details?.field || 'option';
      return `Change ${field}`;
    },
    mergeWindow: 1000
  },

  'number-change': {
    opensGroup: 'edit',
    name: (op: Operation) => {
      const field = (op as UIOperation).details?.field || 'value';
      return `Adjust ${field}`;
    },
    mergeWindow: 2000,
    mergeCondition: (lastOp: Operation, newOp: Operation) => {
      return (lastOp as UIOperation).details?.field === (newOp as UIOperation).details?.field;
    }
  },

  // Node CRUD operations
  'create': {
    opensGroup: 'edit',
    name: (op: Operation) => {
      const nodeType = (op as NodeOperation).data?.type || 'item';
      return `Create ${nodeType}`;
    },
    canMerge: false
  },

  'update': {
    opensGroup: 'edit',
    name: (op: Operation) => {
      const nodeType = (op as NodeOperation).data?.type || 'item';
      return `Update ${nodeType}`;
    },
    mergeWindow: 1500,
    mergeCondition: (lastOp: Operation, newOp: Operation) => {
      // Group updates to same node
      return (lastOp as NodeOperation).nodeId === (newOp as NodeOperation).nodeId;
    }
  },

  'delete': {
    opensGroup: 'edit',
    name: (op: Operation) => {
      const nodeType =
        (op as NodeOperation).deletedTree?.root?.type ||
        (op as NodeOperation).deletedNode?.type ||
        (op as NodeOperation).data?.type ||
        'item';
      return `Delete ${nodeType}`;
    },
    canMerge: false
  },

  'delete-tree': {
    opensGroup: 'edit',
    name: () => 'Delete group and children',
    canMerge: false
  },

  'progress-update': {
    opensGroup: 'edit',
    name: () => 'Update progress',
    mergeWindow: 1500,
    mergeCondition: (lastOp: Operation, newOp: Operation) => {
      return (lastOp as NodeOperation).nodeId === (newOp as NodeOperation).nodeId;
    }
  }
};
